/**
 * BSA — Échelle LSI (Indice de Langelier)
 * Constantes + helpers partagés entre Mes bassins (jauge linéaire)
 * et le wizard water-analyzer (dial semi-circulaire).
 *
 * Expose window.BSA_LSI.
 */

(function () {
  "use strict";

  const LSI_ZONES = [
    { min: -Infinity, max: -0.5, label: 'Corrosive',    class: 'corrosive',   color: '#d63352', bg: '#fce4e8' },
    { min: -0.5,      max: -0.3, label: 'Acide',        class: 'acide',       color: '#b07000', bg: '#fff6e5' },
    { min: -0.3,      max:  0.3, label: 'Équilibrée',   class: 'ok',          color: '#1a8f5a', bg: '#e6f7ef' },
    { min:  0.3,      max:  0.5, label: 'Alcaline',     class: 'alcaline',    color: '#b07000', bg: '#fff6e5' },
    { min:  0.5,      max:  Infinity, label: 'Entartrante', class: 'entartrante', color: '#a36822', bg: '#f5ecdc' },
  ];

  function getLsiZone(lsi) {
    const val = parseFloat(lsi);
    if (isNaN(val)) return null;
    return LSI_ZONES.find((z) => val >= z.min && val < z.max) || LSI_ZONES[LSI_ZONES.length - 1];
  }

  function formatLsi(lsi) {
    const val = parseFloat(lsi);
    if (isNaN(val)) return '—';
    const sign = val < 0 ? '−' : (val > 0 ? '+' : '');
    return sign + Math.abs(val).toFixed(2).replace('.', ',');
  }

  function isLsiValid(lsi) {
    const val = parseFloat(lsi);
    return !isNaN(val) && val >= -5 && val <= 5;
  }

  function renderLsiLinear(lsi) {
    if (!isLsiValid(lsi)) return '';
    const val = parseFloat(lsi);
    const zone = getLsiZone(val);
    if (!zone) return '';
    const pct = Math.max(0, Math.min(100, ((val + 1) / 2) * 100));
    const formatted = formatLsi(val);
    return `
      <div class="bsa-lsi-linear" title="Indice de Langelier">
        <div style="position:relative;">
          <div class="bsa-lsi-linear-track">
            <div class="z1"></div>
            <div class="z2"></div>
            <div class="z3"></div>
            <div class="z4"></div>
            <div class="z5"></div>
          </div>
          <div class="bsa-lsi-linear-dot" style="left:${pct.toFixed(1)}%"></div>
        </div>
        <div class="bsa-lsi-linear-readout">
          <span class="bsa-lsi-linear-value">LSI ${formatted}</span>
          <span class="bsa-lsi-linear-status ${zone.class}">${zone.label}</span>
        </div>
      </div>
    `;
  }

  function renderLsiDial(lsi) {
    if (!isLsiValid(lsi)) {
      return `
        <div class="bsa-lsi-dial-wrap">
          <div class="bsa-lsi-dial-title">Indice de Langelier</div>
          <div style="color:var(--text2);font-size:13px;text-align:center;padding:20px;">
            Valeur non disponible
          </div>
        </div>
      `;
    }

    const val = parseFloat(lsi);
    const zone = getLsiZone(val);
    const formatted = formatLsi(val);
    const offScale = val < -1 || val > 1;

    const clamped = Math.max(-1, Math.min(1, val));
    const angle = 180 - ((clamped + 1) / 2) * 180;
    const rad = (angle * Math.PI) / 180;
    const needleX = 100 + 75 * Math.cos(rad);
    const needleY = 100 - 75 * Math.sin(rad);

    const recommendations = {
      corrosive:   "Eau corrosive — risque d'endommager les surfaces et équipements. Augmenter alcalinité et pH.",
      acide:       "Eau légèrement acide — surveiller l'alcalinité. Protection préventive recommandée.",
      ok:          "Eau bien équilibrée — aucune action nécessaire. Maintenir les paramètres actuels.",
      alcaline:    "Eau légèrement alcaline — surveiller la dureté et le pH. Risque de dépôts à terme.",
      entartrante: "Eau entartrante — risque de dépôts calcaires sur les surfaces et filtres. Abaisser alcalinité et dureté.",
    };

    const offScaleHtml = offScale
      ? `<div class="bsa-lsi-dial-offscale ${val < -1 ? 'corrosive' : 'entartrante'}">
           Hors échelle — ${val < -1 ? 'Eau très corrosive' : 'Eau très entartrante'} (LSI ${formatted})
         </div>`
      : '';

    return `
      <div class="bsa-lsi-dial-wrap">
        <div class="bsa-lsi-dial-title">Indice de Langelier (LSI)</div>
        <div class="bsa-lsi-dial-body">
          <div class="bsa-lsi-dial" aria-label="Indice Langelier ${formatted}, zone ${zone.label}">
            <svg viewBox="0 0 200 110" xmlns="http://www.w3.org/2000/svg" role="img">
              <path d="M 15 100 A 85 85 0 0 1 38 40"   stroke="#d63352" stroke-width="18" fill="none" stroke-linecap="butt"/>
              <path d="M 38 40  A 85 85 0 0 1 64 20"   stroke="#f5a522" stroke-width="18" fill="none"/>
              <path d="M 64 20  A 85 85 0 0 1 136 20"  stroke="#1a8f5a" stroke-width="18" fill="none"/>
              <path d="M 136 20 A 85 85 0 0 1 162 40"  stroke="#f5a522" stroke-width="18" fill="none"/>
              <path d="M 162 40 A 85 85 0 0 1 185 100" stroke="#a36822" stroke-width="18" fill="none"/>
              <line x1="100" y1="100" x2="${needleX.toFixed(1)}" y2="${needleY.toFixed(1)}"
                    stroke="#0d2d5e" stroke-width="3" stroke-linecap="round"/>
              <circle cx="100" cy="100" r="6" fill="#0d2d5e"/>
              <text x="15" y="108" font-size="9" fill="#4a6490" text-anchor="middle" font-family="monospace">−1.0</text>
              <text x="100" y="15" font-size="9" fill="#4a6490" text-anchor="middle" font-family="monospace">0</text>
              <text x="185" y="108" font-size="9" fill="#4a6490" text-anchor="middle" font-family="monospace">+1.0</text>
            </svg>
            <div class="bsa-lsi-dial-center">
              <div class="bsa-lsi-dial-num">${formatted}</div>
              <div class="bsa-lsi-dial-label">LSI</div>
            </div>
          </div>
          <div class="bsa-lsi-dial-info">
            <div class="bsa-lsi-dial-status" style="color:${zone.color}">${zone.label}</div>
            <div class="bsa-lsi-dial-desc">
              L'indice de Langelier évalue l'équilibre chimique de l'eau.
              Zone idéale : <strong>−0,3 à +0,3</strong>.
            </div>
            <div class="bsa-lsi-dial-recom ${zone.class}">
              ${recommendations[zone.class]}
            </div>
            ${offScaleHtml}
          </div>
        </div>
      </div>
    `;
  }

  window.BSA_LSI = {
    LSI_ZONES,
    getLsiZone,
    formatLsi,
    isLsiValid,
    renderLsiLinear,
    renderLsiDial,
  };
})();
